/**
 * pages/Newsfeed.js — Fact-checked post feed with read receipt tracking
 * ======================================================================
 * Shows a filterable list of fact-checked posts for the logged-in user.
 * Clicking a post opens the ModalContent component for full details and,
 * for FACT_CHECKER role, in-place editing.
 *
 * Filter controls
 * ----------------
 * - Label radio   : Fake | Real | (Spam | Blank for FACT_CHECKER/ADMIN)
 * - State select  : populated from usr.states (regions the user has access to)
 * - Date range    : defaults to the last 7 days; Ant Design RangePicker
 *
 * Read receipt logic
 * -------------------
 * A post is marked `viewed` by the backend when:
 *   1. The user has a stats record for this (user, post) pair, AND
 *   2. The stored fc_ts in the receipt matches the current post's fc_ts.
 *      (If the post was re-reviewed since last read, viewed = false to
 *       prompt the user to re-read the updated verdict.)
 *
 * When a post is opened (closeModal called with viewed=false), a read receipt
 * is recorded via apiClient.recordReadReceipt().  This updates the stats index
 * and contributes to the engagement distribution charts on the Home dashboard.
 *
 * Fact-checker workflow
 * ----------------------
 * FACT_CHECKER and ADMIN roles see all four label types (Blank, Real, Fake, Spam).
 * Opening a post within the ±10-day edit window (isvalidforedit in Modal.js)
 * enables the verdict selector and explanation editor.  Saving calls
 * apiClient.updatePost() and optimistically removes the post from the current
 * filter list if the new label differs from the active radio selection.
 *
 * API calls
 * ----------
 * apiClient.getNewsfeed(...)      — POST /session/newsfeed
 * apiClient.fetchPost(addr)       — POST /session/fetch_post
 * apiClient.recordReadReceipt(…)  — POST /session/record_read_receipt
 * apiClient.updatePost(addr, {…}) — POST /session/update_post
 */

import { useState, useEffect, useMemo } from "react";
import { Col, Row, Flex } from "antd";
import { Radio, DatePicker, List, Tag, message, Select, Button, Spin, Space } from "antd";
import { LockOutlined, UnlockOutlined, EnvironmentOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { subDays, addDays } from "date-fns";
import InfiniteScroll from "react-infinite-scroll-component";
import apiClient from "../utils/apiClient";
import { ModalContent } from "./components/Modal";
import { useLoc, useUsr } from "../App";
import "./newsfeed.css";

const { RangePicker } = DatePicker;

// Ant Design tag colour map for post labels
const LBL_COLOR = { BLANK: "blue", REAL: "success", FAKE: "error", SPAM: "warning" };


function formatTs(ts) {
    if (!ts) return { date: '', relative: '', iso: '' };
    try {
        const dt = new Date(ts);
        const h = dt.getHours(); const ampm = h >= 12 ? "PM" : "AM"; const h12 = h % 12 || 12;
        const time = String(h12).padStart(2, "0") + ":" + String(dt.getMinutes()).padStart(2, "0") + ":" + String(dt.getSeconds()).padStart(2, "0") + " " + ampm;
        const date = dt.getFullYear() + "-" + String(dt.getMonth() + 1).padStart(2, "0") + "-" + String(dt.getDate()).padStart(2, "0") + " " + time;
        const diff = Date.now() - dt.getTime();
        const mins = Math.floor(diff / 60000);
        const hrs  = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        const wks  = Math.floor(days / 7);
        const mos  = Math.floor(days / 30);
        const yrs  = Math.floor(days / 365);
        const relative =
            mins < 60   ? `${mins} minute${mins !== 1 ? "s" : ""} ago` :
            hrs  < 24   ? `${hrs} hour${hrs !== 1 ? "s" : ""} ago`     :
            days < 7    ? `${days} day${days !== 1 ? "s" : ""} ago`    :
            wks  < 4    ? `${wks} week${wks !== 1 ? "s" : ""} ago`     :
            mos  < 12   ? `${mos} month${mos !== 1 ? "s" : ""} ago`    :
                          `${yrs} year${yrs !== 1 ? "s" : ""} ago`;
        return { date, relative, iso: dt.toISOString() };
    } catch { return { date: ts, relative: '', iso: ts }; }
}

/**
 * getDate — returns a 'YYYY-MM-DD' string for today ± delta days.
 * delta=0 → today, delta=7 → 7 days ago.
 * Uses addDays(+1) first to include today in the range.
 *
 * @param {number} delta — number of days before today.
 * @returns {string}
 */
function getDate(delta) {
    const dt = subDays(addDays(new Date(), 1), delta);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return y + "-" + m + "-" + d;
}

export const Newsfeed = () => {
    const { usr } = useUsr();
    const { loc } = useLoc();

    // Filter state — all four values are restored from localStorage on mount so
    // navigating away and back preserves the user's last selection for the session.
    const [newstype, setNewstype] = useState(() => localStorage.getItem('fnma_nf_newstype') || 'Fake');
    const [selected_state, selectState] = useState(() => {
        const raw = localStorage.getItem('fnma_nf_state');
        if (raw === null) return usr.state;
        try { return JSON.parse(raw); } catch { return usr.state; }
    });
    const [from, setFrom] = useState(() => localStorage.getItem('fnma_nf_from') || getDate(7));
    const [to, setTo]     = useState(() => localStorage.getItem('fnma_nf_to')   || getDate(0));

    // Data state
    const [loading, setLoading] = useState(false);
    const [queryres, setQueryres] = useState([]);   // list of posts from backend
    const [post, setPost] = useState(null);         // full post detail for modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPostViewed, setisPostViewed] = useState(null); // was the clicked post already viewed?
    const [modalReactions, setModalReactions] = useState({ likes: 0, dislikes: 0, severityCount: 0, userAffinity: null, userSeverity: null });

    const [messageApi, messageHolder] = message.useMessage();

    // 4 independent sort states — all restored from localStorage for the session.
    // Priority is fixed left-to-right: Date → Severity → Approval → Disapproval
    //
    // Initializer logic — three cases from getItem:
    //   null  → key never written (first visit) → apply the default value
    //   ''    → user explicitly cleared this sort via ×  → restore as undefined (Select shows placeholder)
    //   'x'   → a real sort value → restore it directly
    const [sortDate, setSortDate] = useState(() => { const v = localStorage.getItem('fnma_nf_sort_date'); return v === null ? 'date_desc' : v || undefined; });
    const [sortSev,  setSortSev]  = useState(() => { const v = localStorage.getItem('fnma_nf_sort_sev');  return v === null ? 'sev_desc'  : v || undefined; });
    const [sortApr,  setSortApr]  = useState(() => { const v = localStorage.getItem('fnma_nf_sort_apr');  return v === null ? 'apr_desc'  : v || undefined; });
    const [sortDis,  setSortDis]  = useState(() => { const v = localStorage.getItem('fnma_nf_sort_dis');  return v === null ? 'dis_asc'   : v || undefined; });

    // Compound sort applied client-side; null entries are skipped.
    // Priority order: Date (1) → Severity (2) → Approval (3) → Disapproval (4).
    // Reaction counts strip the current user's own vote so sort reflects community consensus only.
    const sortedQueryres = useMemo(() => {
        const keys = [sortDate, sortSev, sortApr, sortDis].filter(Boolean);
        if (keys.length === 0) return queryres;
        const othersLikes    = p => (p.likes         ?? 0) - (p.user_affinity === 1  ? 1 : 0);
        const othersDislikes = p => (p.dislikes       ?? 0) - (p.user_affinity === -1 ? 1 : 0);
        const othersSev      = p => (p.severity_count ?? 0) - (p.user_severity === 1  ? 1 : 0);
        return [...queryres].sort((a, b) => {
            for (const key of keys) {
                let diff = 0;
                // Date comparison uses the YYYY-MM-DD prefix only so that severity,
                // approval, and disapproval resolve ties within the same calendar day.
                if      (key === 'date_desc') diff = b.ts.slice(0, 10).localeCompare(a.ts.slice(0, 10));
                else if (key === 'date_asc')  diff = a.ts.slice(0, 10).localeCompare(b.ts.slice(0, 10));
                else if (key === 'sev_desc')  diff = othersSev(b)      - othersSev(a);
                else if (key === 'sev_asc')   diff = othersSev(a)      - othersSev(b);
                else if (key === 'apr_desc')  diff = othersLikes(b)    - othersLikes(a);
                else if (key === 'apr_asc')   diff = othersLikes(a)    - othersLikes(b);
                else if (key === 'dis_desc')  diff = othersDislikes(b) - othersDislikes(a);
                else if (key === 'dis_asc')   diff = othersDislikes(a) - othersDislikes(b);
                if (diff !== 0) return diff;
            }
            return 0;
        });
    }, [queryres, sortDate, sortSev, sortApr, sortDis]);

    // Privileged roles see all four labels; regular users only see Fake and Real
    const isPrivileged = usr.role === "FACT_CHECKER";
    const isAdmin = usr.role === "ADMIN";
    const radioOptions = (isPrivileged || isAdmin) ? ["Fake", "Real", "Spam", "Blank"] : ["Fake", "Real"];

    // Fetch the newsfeed whenever any filter changes or the token changes.
    useEffect(() => {
        if (!usr.token) return;
        setLoading(true);
        apiClient.getNewsfeed(usr.addr, from, to, newstype.toUpperCase(), selected_state)
            .then(response => {
                setQueryres(response.data.hasOwnProperty("results") ? response.data.results : []);
            })
            .catch(error => {
                console.error("Error fetching newsfeed:", error);
                if (error.response?.status === 401) messageApi.error("Session expired. Please login again");
            })
            .finally(() => setLoading(false));
    }, [messageApi, newstype, from, to, usr.token, usr.addr, selected_state]);

    /**
     * openModal — fetches full post detail and opens the modal.
     * Stores the `viewed` flag so closeModal knows whether to record a receipt.
     *
     * @param {string}  addr   — '<index>/<id>' post address.
     * @param {boolean} viewed — whether this user has already read this post.
     */
    const openModal = (addr, viewed, likes, dislikes, severityCount, userAffinity, userSeverity) => {
        setisPostViewed(viewed);
        setModalReactions({ likes: likes ?? 0, dislikes: dislikes ?? 0, severityCount: severityCount ?? 0, userAffinity: userAffinity ?? null, userSeverity: userSeverity ?? null });
        apiClient.fetchPost(addr)
            .then(response => { if (response.data.hasOwnProperty("lbl")) setPost(response.data); })
            .catch(error => {
                console.error("Error fetching post:", error);
                if (error.response?.status === 401) messageApi.error("Session expired. Please login again");
            });
        setIsModalOpen(true);
    };

    const onModalReact = (type, value) => {
        if (!post) return;
        setQueryres(prev => prev.map(p => {
            if (p.addr !== post.addr) return p;
            if (type === 'affinity') {
                const prev_aff = p.user_affinity;
                return { ...p, user_affinity: value || null,
                    likes:    value === 1  ? p.likes + 1    : (prev_aff === 1  ? p.likes - 1    : p.likes),
                    dislikes: value === -1 ? p.dislikes + 1 : (prev_aff === -1 ? p.dislikes - 1 : p.dislikes) };
            }
            return { ...p, user_severity: value || null, severity_count: p.severity_count + (value === 1 ? 1 : -1) };
        }));
        if (type === 'affinity') {
            apiClient.recordStats(usr.addr, post.addr, post.lbl, usr.role,
                { affinity: value, affinity_fc_ts: post.fc_ts })
                .then(res => {
                    if (res.data.released !== undefined) {
                        setQueryres(prev => prev.map(p => p.addr === post.addr ? { ...p, released: res.data.released, approvers: res.data.approvers ?? p.approvers } : p));
                        setPost(prev => prev ? { ...prev, released: res.data.released, approvers: res.data.approvers ?? prev.approvers } : prev);
                    }
                });
        } else {
            apiClient.recordStats(usr.addr, post.addr, post.lbl, usr.role,
                { severity: value });
        }
    };

    /**
     * closeModal — pure dismiss with no side effects.
     * Receipt recording is button-only for all roles (see ackPost / submitModel).
     */
    const closeModal = () => {
        setPost(null); setisPostViewed(null); setIsModalOpen(false);
    };

    /**
     * ackPost — called only when a regular user explicitly clicks "Got it".
     *
     * Records the read receipt and optimistically marks the post as viewed.
     * FACT_CHECKERs never reach this path — their receipt is handled in closeModal.
     */
    const ackPost = () => {
        if (post !== null && ["REAL", "FAKE"].includes(post.lbl) && isPostViewed === false) {
            setQueryres(prev => prev.map(item => item.addr === post.addr ? { ...item, viewed: true } : item));
            apiClient.recordStats(usr.addr, post.addr, post.lbl, usr.role,
                { viewed: true, loc: [loc.lon, loc.lat] })
                .then(() => messageApi.open({ type: "success", content: "Read receipt recorded" }))
                .catch(error => {
                    console.error("Error recording receipt:", error);
                    if (error.response?.status === 401) messageApi.error("Session expired. Please login again");
                });
        }
        setPost(null); setisPostViewed(null); setIsModalOpen(false);
    };

    /**
     * submitModel — called by ModalContent when a FACT_CHECKER saves changes.
     *
     * Sends the updated label, explanation, and files to the backend.
     * If the new label differs from the active radio filter, the post is
     * removed from the current list (it no longer matches the filter).
     * The post is also marked viewed=false so the updated verdict prompts
     * other users to re-read it.
     */
    const submitModel = () => {
        if (post !== null) {
            // If the fact-checker changed the label, remove from current filtered list
            if (post.lbl !== newstype.toUpperCase()) setQueryres(queryres.filter(item => item.addr !== post.addr));
            apiClient.updatePost(post.addr, { lbl: post.lbl, exp: post.exp, files: post.files })
                .then(response => {
                    if (response.data.hasOwnProperty("addr")) {
                        // fc_ts changed on the server — mark as unread and immediately
                        // reset all version-dependent signals so the card reflects the
                        // new version without waiting for a page reload
                        setQueryres(prev => prev.map(item => item.addr === post.addr ? {
                            ...item,
                            viewed:       false,
                            likes:        0,
                            dislikes:     0,
                            user_affinity: 0,
                        } : item));
                    }
                })
                .catch(error => {
                    console.error("Error updating post:", error);
                    if (error.response?.status === 401) messageApi.error("Session expired. Please login again");
                });

            // EXPERIMENTAL: delay recordStats instead of sequencing it after
            // updatePost, to test whether a fixed gap is enough to avoid the
            // fc_ts race (updatePost's write landing before record_stats'
            // server-side re-read of the post). Unlike proper sequencing this
            // is a heuristic, not a guarantee — a slow network/server could
            // still make updatePost take longer than this delay, reintroducing
            // the race. Verify against real submissions (check version_ts in
            // Kibana across several tries) before relying on this.
            setTimeout(() => {
                // Record read receipt for the FC — saving counts as an intentional read
                if (["REAL", "FAKE"].includes(post.lbl) && isPostViewed === false) {
                    setQueryres(prev => prev.map(item => item.addr === post.addr ? { ...item, viewed: true } : item));
                    apiClient.recordStats(usr.addr, post.addr, post.lbl, usr.role,
                        { viewed: true, loc: [loc.lon, loc.lat] })
                        .catch(error => {
                            console.error("Error recording receipt:", error);
                            if (error.response?.status === 401) messageApi.error("Session expired. Please login again");
                        });
                }
            }, 500);
        }
        setPost(null); setisPostViewed(null); setIsModalOpen(false);
        messageApi.open({ type: "success", content: "Fact checked successfully" });
    };

    return (
        <div className="page-container">
            {messageHolder}
            {/* Modal is only mounted when post data is available to prevent rendering with null props */}
            {post && <ModalContent isOpen={isModalOpen} onClose={closeModal} inpPost={post} onSubmit={submitModel} onAck={ackPost} onReact={onModalReact} likes={modalReactions.likes} dislikes={modalReactions.dislikes} severityCount={modalReactions.severityCount} userAffinity={modalReactions.userAffinity} userSeverity={modalReactions.userSeverity} />}

            {/* ----------------------------------------------------------------
                Filter controls — label radio, state dropdown, date range picker
            ---------------------------------------------------------------- */}
            <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
                {/* Label filter — privileged users see Spam/Blank; others see Fake/Real only */}
                <Col xs={24} sm={8}>
                    <Radio.Group value={newstype} onChange={e => { setNewstype(e.target.value); localStorage.setItem('fnma_nf_newstype', e.target.value); }} optionType="button" buttonStyle="solid">
                        {radioOptions.map(opt => <Radio.Button key={opt} value={opt}>{opt}</Radio.Button>)}
                    </Radio.Group>
                </Col>

                {/* State filter — pin button auto-fills from login location; Select is searchable + clearable */}
                <Col xs={24} sm={8}>
                    <Flex justify="center">
                        <Space.Compact style={{ width: "100%", maxWidth: 400 }}>
                            <Button
                                icon={<EnvironmentOutlined />}
                                title="Auto-fill from login location"
                                disabled={!usr.token}
                                onClick={() => {
                                    if (usr.state) {
                                        selectState(usr.state);
                                        localStorage.setItem('fnma_nf_state', JSON.stringify(usr.state));
                                    } else {
                                        messageApi.info('Location unavailable — select a state manually');
                                    }
                                }}
                            />
                            <Select
                                value={selected_state}
                                style={{ width: "100%", textAlign: "center" }}
                                dropdownStyle={{ textAlign: "center" }}
                                className="state-select-centered"
                                placeholder="All States"
                                showSearch
                                allowClear
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={value => {
                                    const v = value ?? null;
                                    selectState(v);
                                    localStorage.setItem('fnma_nf_state', JSON.stringify(v));
                                }}
                                options={usr.states.map(state => ({ value: state, label: state }))}
                            />
                        </Space.Compact>
                    </Flex>
                </Col>

                {/* Date range picker — defaults to last 7 days */}
                <Col xs={24} sm={8}>
                    <Flex justify="flex-end">
                        <RangePicker
                            defaultValue={[dayjs(from, "YYYY-MM-DD"), dayjs(to, "YYYY-MM-DD")]}
                            onChange={(_, strs) => { setFrom(strs[0]); setTo(strs[1]); localStorage.setItem('fnma_nf_from', strs[0]); localStorage.setItem('fnma_nf_to', strs[1]); }}
                        />
                    </Flex>
                </Col>
            </Row>

            {/* ----------------------------------------------------------------
                Post list — height-bounded scroll container.
                active-item / inactive-item CSS classes (newsfeed.css) give
                a visual difference between viewed and unviewed posts.
            ---------------------------------------------------------------- */}
            {/* Sort row — visual order matches logical priority: Date → Severity → Approval → Disapproval */}
            <Row gutter={[12, 8]} align="middle" style={{ marginBottom: 8 }}>
                <Col xs={12} sm={6}>
                    <Select value={sortDate} onChange={v => { setSortDate(v); localStorage.setItem('fnma_nf_sort_date', v ?? ''); }} style={{ width: "100%" }} allowClear placeholder="Date"
                        options={[{ value: 'date_desc', label: 'Newest first' }, { value: 'date_asc', label: 'Oldest first' }]}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <Select value={sortSev} onChange={v => { setSortSev(v); localStorage.setItem('fnma_nf_sort_sev', v ?? ''); }} style={{ width: "100%" }} allowClear placeholder="Severity"
                        options={[{ value: 'sev_desc', label: 'Severity ↓' }, { value: 'sev_asc', label: 'Severity ↑' }]}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <Select value={sortApr} onChange={v => { setSortApr(v); localStorage.setItem('fnma_nf_sort_apr', v ?? ''); }} style={{ width: "100%" }} allowClear placeholder="Approval"
                        options={[{ value: 'apr_desc', label: 'Approval ↓' }, { value: 'apr_asc', label: 'Approval ↑' }]}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <Select value={sortDis} onChange={v => { setSortDis(v); localStorage.setItem('fnma_nf_sort_dis', v ?? ''); }} style={{ width: "100%" }} allowClear placeholder="Disapproval"
                        options={[{ value: 'dis_desc', label: 'Disapproval ↓' }, { value: 'dis_asc', label: 'Disapproval ↑' }]}
                    />
                </Col>
            </Row>

            <Spin spinning={loading} tip="Loading feed…">
            <InfiniteScroll style={{ height: "80vh", background: "#f5f5f5", padding: "8px 0" }} dataLength={sortedQueryres.length} scrollableTarget="scrollableDiv">
                <List
                    split={false}
                    dataSource={sortedQueryres}
                    locale={{ emptyText: <span style={{ color: "#8c8c8c", display: "block", padding: "40px 0", textAlign: "center" }}>No items for this filter</span> }}
                    renderItem={item => (
                        <List.Item
                            key={item.addr}
                            className={item.viewed ? "active-item" : "inactive-item"}
                            style={{ padding: "12px 16px", marginBottom: 8 }}
                        >
                            {(() => {
                                const { date, relative } = formatTs(item.ts);
                                const isReleased = item.released && item.lbl !== "BLANK";
                                const dateTagRow = (
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                                            {date}{relative && ` (${relative})`}{item.reg_state && `, ${item.reg_state}`}
                                        </span>
                                        <Tag color={LBL_COLOR[item.lbl] || "default"} style={{ margin: 0 }}>{item.lbl}</Tag>
                                    </div>
                                );
                                const statsRow = (
                                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }} onClick={e => e.stopPropagation()}>
                                        {item.lbl !== "BLANK" && <span style={{ fontSize: 12, color: "#52c41a" }}>👍 <span style={{ display: "inline-block", minWidth: 32, textAlign: "left" }}>{item.likes ?? 0}</span></span>}
                                        <span style={{ fontSize: 12, color: "#1677ff" }}>🚩 <span style={{ display: "inline-block", minWidth: 32, textAlign: "left" }}>{item.severity_count ?? 0}</span></span>
                                        {item.lbl !== "BLANK" && <span style={{ fontSize: 12, color: "#ff4d4f" }}>👎 <span style={{ display: "inline-block", minWidth: 32, textAlign: "left" }}>{item.dislikes ?? 0}</span></span>}
                                    </div>
                                );
                                const reactionRow = (
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={e => e.stopPropagation()}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {isPrivileged && item.lbl !== "BLANK" && (
                                                isReleased
                                                    ? <LockOutlined style={{ fontSize: 18, color: "#FFD700", filter: "drop-shadow(0 0 3px rgba(255, 215, 0, 0.85))" }} title="Released — locked for editing" />
                                                    : <UnlockOutlined style={{ fontSize: 18, color: "#8c8c8c" }} title="Pending approval — editable" />
                                            )}
                                            {isAdmin && isReleased && (
                                                <Button size="small" danger onClick={() => {
                                                    apiClient.revertPost(item.addr)
                                                        .then(() => {
                                                            setQueryres(prev => prev.filter(p => p.addr !== item.addr));
                                                            messageApi.success("Post reverted to BLANK");
                                                        })
                                                        .catch(() => messageApi.error("Revert failed"));
                                                }}>Revert</Button>
                                            )}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            {item.lbl !== "BLANK" && (
                                                <span style={{ fontSize: 13, cursor: "pointer", color: "#52c41a", userSelect: "none" }}
                                                    onClick={() => {
                                                        const next = item.user_affinity === 1 ? 0 : 1;
                                                        setQueryres(prev => prev.map(p => p.addr === item.addr ? { ...p, user_affinity: next, likes: p.likes + (next === 1 ? 1 : -1), dislikes: p.user_affinity === -1 ? p.dislikes - 1 : p.dislikes } : p));
                                                        apiClient.recordStats(usr.addr, item.addr, item.lbl, usr.role,
                                                            { affinity: next, affinity_fc_ts: item.fc_ts })
                                                            .then(res => {
                                                                if (res.data.released !== undefined)
                                                                    setQueryres(prev => prev.map(p => p.addr === item.addr ? { ...p, released: res.data.released, approvers: res.data.approvers ?? p.approvers } : p));
                                                            });
                                                    }}
                                                >👍 <span style={{ display: "inline-block", minWidth: 32, textAlign: "left" }}>{item.user_affinity === 1 ? `${(item.likes ?? 1) - 1}+1` : (item.likes ?? 0)}</span></span>
                                            )}
                                            <span style={{ fontSize: 13, cursor: "pointer", color: "#1677ff", userSelect: "none" }}
                                                onClick={() => {
                                                    const next = item.user_severity === 1 ? 0 : 1;
                                                    setQueryres(prev => prev.map(p => p.addr === item.addr ? { ...p, user_severity: next, severity_count: p.severity_count + (next === 1 ? 1 : -1) } : p));
                                                    apiClient.recordStats(usr.addr, item.addr, item.lbl, usr.role,
                                                        { severity: next });
                                                }}
                                            >🚩 <span style={{ display: "inline-block", minWidth: 32, textAlign: "left" }}>{item.user_severity === 1 ? `${(item.severity_count ?? 1) - 1}+1` : (item.severity_count ?? 0)}</span></span>
                                            {item.lbl !== "BLANK" && (
                                                <span style={{ fontSize: 13, cursor: "pointer", color: "#ff4d4f", userSelect: "none" }}
                                                    onClick={() => {
                                                        const next = item.user_affinity === -1 ? 0 : -1;
                                                        setQueryres(prev => prev.map(p => p.addr === item.addr ? { ...p, user_affinity: next, dislikes: p.dislikes + (next === -1 ? 1 : -1), likes: p.user_affinity === 1 ? p.likes - 1 : p.likes } : p));
                                                        apiClient.recordStats(usr.addr, item.addr, item.lbl, usr.role,
                                                            { affinity: next, affinity_fc_ts: item.fc_ts })
                                                            .then(res => {
                                                                if (res.data.released !== undefined)
                                                                    setQueryres(prev => prev.map(p => p.addr === item.addr ? { ...p, released: res.data.released, approvers: res.data.approvers ?? p.approvers } : p));
                                                            });
                                                    }}
                                                >👎 <span style={{ display: "inline-block", minWidth: 32, textAlign: "left" }}>{item.user_affinity === -1 ? `${(item.dislikes ?? 1) - 1}+1` : (item.dislikes ?? 0)}</span></span>
                                            )}
                                        </div>
                                    </div>
                                );
                                // FC/ADMIN: date+tag → divider → claim+attachments → divider → reactions
                                // USER:     stats    → divider → claim+attachments → divider → date+tag
                                return (
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        {(isPrivileged || isAdmin) ? dateTagRow : statsRow}
                                        <div style={{ borderBottom: "1px solid #f0f0f0", margin: "6px 0" }} />
                                        <div className="post-content" style={{ cursor: "pointer" }} onClick={() => openModal(item.addr, item.viewed, item.likes, item.dislikes, item.severity_count, item.user_affinity, item.user_severity)}>
                                            <p style={{ wordBreak: "break-word", overflowWrap: "break-word", margin: 0, fontSize: 14 }}>
                                                {(item.msg ?? '').replace(/\s+/g, ' ').trim()}
                                            </p>
                                            {item.claim_files?.length > 0 && (
                                                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                                                    {item.claim_files.map((f, i) => {
                                                        const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(f.url);
                                                        return isVideo && !f.thumbUrl ? (
                                                            <div key={i} style={{ width: 48, height: 48, borderRadius: 4, border: "1px solid #e8e8e8", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                <span style={{ color: "#fff", fontSize: 18 }}>▶</span>
                                                            </div>
                                                        ) : (
                                                            <img key={i} src={f.thumbUrl || f.url} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, border: "1px solid #e8e8e8" }} />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ borderBottom: "1px solid #f0f0f0", margin: "6px 0" }} />
                                        {(isPrivileged || isAdmin) ? reactionRow : dateTagRow}
                                    </div>
                                );
                            })()}
                        </List.Item>
                    )}
                />
            </InfiniteScroll>
            </Spin>
        </div>
    );
};
